/**
 * Session-file client for the Fleet Control UI.
 *
 * A session file is an inbound operator attachment stored server-side and
 * keyed by session id. The browser never keeps the bytes: it uploads once
 * at attach time and then refers to the file by id everywhere else —
 * the composer chip, the transcript bubble, the Files tab, and the
 * subagent header. That is what makes the attachment survive a reload and
 * what lets a later turn say "the video already uploaded".
 *
 * The counterpart on the gateway is `src/gateway/session-files/`.
 */

import type { IconName } from "../icons.ts";
import { inferBasePathFromPathname } from "../navigation.ts";
import { attachmentIconNameForKind } from "./attachment-types.ts";

/** Canonical path the gateway mounts (also accepted under a basePath). */
export const SESSION_FILES_PATH = "/api/session/files";

/**
 * Per-file cap, mirrored from `MAX_SESSION_FILE_BYTES` on the gateway.
 *
 * The composer rejects locally so the operator sees the problem on the chip
 * they just dropped, before any bytes leave the browser. The server enforces
 * the same number independently — this copy is UX, not security.
 */
export const MAX_SESSION_FILE_BYTES = 25 * 1024 * 1024;

/** Per-message cap. Mirrors the gateway's per-session count limit loosely. */
export const MAX_SESSION_FILES_PER_MESSAGE = 20;

/** What the gateway records for one stored file. */
export interface SessionFileRecord {
  fileId: string;
  sessionId: string;
  filename: string;
  mediaType: string;
  size: number;
  sha256: string;
  kind: SessionFileKind;
  createdAt: number;
  messageId?: string;
  previewMediaType?: string;
  prep?: SessionFilePrepSummary;
}

export type SessionFileKind =
  | "image"
  | "video"
  | "audio"
  | "document"
  | "tabular"
  | "text"
  | "binary";

export interface SessionFilePrepSummary {
  preparedAt: number;
  notes: string[];
  derivatives: string[];
  durationSeconds?: number;
  frameCount?: number;
  pageCount?: number;
  textChars?: number;
  columns?: Array<{ name: string; type: string }>;
  rowCount?: number;
  sampleRows?: string[][];
  binaryOnly?: boolean;
}

/** Why an attach was refused. Never a type. */
export type SessionFileRejectCode = "too_large" | "too_many" | "unreadable" | "empty" | "network";

export class SessionFileError extends Error {
  readonly code: SessionFileRejectCode;
  constructor(code: SessionFileRejectCode, message: string) {
    super(message);
    this.code = code;
    this.name = "SessionFileError";
  }
}

// ---------------------------------------------------------------------------
// URL building
// ---------------------------------------------------------------------------

function normalizeBasePath(basePath: string | undefined): string {
  let base = (basePath ?? "").trim();
  if (!base) return "";
  if (!base.startsWith("/")) base = `/${base}`;
  if (base === "/") return "";
  return base.endsWith("/") ? base.slice(0, -1) : base;
}

/**
 * Resolve the Control UI base path.
 *
 * The UI is often served under `gateway.controlUi.basePath` behind a proxy
 * that only forwards that prefix, so a bare `/api/session/files` would 404.
 */
export function resolveSessionFilesBasePath(explicit?: string): string {
  if (explicit !== undefined) return normalizeBasePath(explicit);
  if (typeof window === "undefined") return "";
  const configured = window.__OPENCLAW_CONTROL_UI_BASE_PATH__;
  if (typeof configured === "string" && configured.trim()) return normalizeBasePath(configured);
  try {
    return normalizeBasePath(inferBasePathFromPathname(window.location.pathname));
  } catch {
    return "";
  }
}

/** Absolute URL for the collection, a list, or one file's bytes. */
export function buildSessionFileUrl(args: {
  origin: string;
  basePath?: string;
  sessionKey?: string;
  fileId?: string;
  /**
   * Which derivative of the file to address.
   *
   *   - `raw`     — the original bytes (default).
   *   - `preview` — the downscaled thumbnail derivative.
   *   - `content` — JAISIU-4448: the **prepared preview payload** as
   *     JSON (tabular columns + sample rows, or extracted document
   *     text). Not the original bytes — the gateway's prepared
   *     derivatives, which is what a spreadsheet table / document text
   *     view needs.
   */
  variant?: "raw" | "preview" | "content";
  /**
   * JAISIU-4435 — ask the gateway for a specific `Content-Disposition`.
   *
   * The gateway already defaults to `inline` for the media types the app
   * can render (PDF, image, audio, video), which is what makes the PDF
   * preview `<iframe>` work instead of triggering a download. Passing
   * `"inline"` explicitly makes that intent visible at the call site and
   * keeps the preview working even if the gateway default changes.
   */
  disposition?: "inline" | "attachment";
}): string {
  const root = (args.origin ?? "").replace(/\/+$/, "");
  let path = `${root}${resolveSessionFilesBasePath(args.basePath)}${SESSION_FILES_PATH}`;
  if (args.fileId) {
    path += `/${encodeURIComponent(args.fileId)}/${args.variant ?? "raw"}`;
  }
  const query: string[] = [];
  if (args.sessionKey) {
    query.push(`sessionKey=${encodeURIComponent(args.sessionKey)}`);
  }
  if (args.disposition) {
    query.push(`disposition=${args.disposition}`);
  }
  if (query.length > 0) {
    path += `?${query.join("&")}`;
  }
  return path;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

function authHeaders(token: string | undefined): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Map an HTTP status + body onto a typed, human-readable rejection. */
async function rejectFromResponse(resp: Response, filename: string): Promise<SessionFileError> {
  let code: SessionFileRejectCode = "unreadable";
  let message = "";
  try {
    const json = (await resp.json()) as { code?: string; error?: string };
    if (json.code) code = json.code as SessionFileRejectCode;
    if (typeof json.error === "string" && json.error.trim()) message = json.error.trim();
  } catch {
    // Non-JSON error body — fall through to the status-derived message.
  }
  if (!message) {
    message =
      resp.status === 413
        ? "file is too large"
        : resp.status === 409
          ? "this message already has too many files"
          : `upload failed (${resp.status})`;
  }
  // 415 is a type rejection. The session-file store never sends it; if one
  // ever arrives it is a bug we want visible rather than a wrong message.
  if (resp.status === 415) {
    return new SessionFileError("unreadable", `gateway rejected the file type: ${message}`);
  }
  void filename;
  return new SessionFileError(code, message);
}

/**
 * Upload one file into the session's store. Throws {@link SessionFileError}
 * on a rejection so the composer can mark exactly this chip.
 */
export async function uploadSessionFile(args: {
  origin: string;
  token: string | undefined;
  file: File;
  sessionKey: string;
  basePath?: string;
}): Promise<SessionFileRecord> {
  if (!args.sessionKey) {
    throw new SessionFileError("unreadable", "no session selected");
  }
  if (args.file.size === 0) {
    throw new SessionFileError("empty", "file is empty");
  }
  if (args.file.size > MAX_SESSION_FILE_BYTES) {
    throw new SessionFileError(
      "too_large",
      `file is ${formatAttachmentBytes(args.file.size)} — the limit is ${formatAttachmentBytes(MAX_SESSION_FILE_BYTES)}`,
    );
  }

  const form = new FormData();
  form.append("file", args.file, basenameForUpload(args.file.name));
  form.append("sessionKey", args.sessionKey);

  let resp: Response;
  try {
    resp = await fetch(buildSessionFileUrl({ origin: args.origin, basePath: args.basePath }), {
      method: "POST",
      headers: authHeaders(args.token),
      body: form,
      credentials: "include",
    });
  } catch (err) {
    throw new SessionFileError(
      "network",
      err instanceof Error ? err.message : "could not reach the gateway",
    );
  }
  if (!resp.ok) {
    throw await rejectFromResponse(resp, args.file.name);
  }
  const json = (await resp.json()) as { ok?: boolean; file?: SessionFileRecord };
  if (json.ok !== true || !json.file) {
    throw new SessionFileError("unreadable", "gateway did not return a stored file record");
  }
  return json.file;
}

/** Every file stored for a session, oldest first. */
export async function listSessionFiles(args: {
  origin: string;
  token: string | undefined;
  sessionKey: string;
  basePath?: string;
}): Promise<SessionFileRecord[]> {
  if (!args.sessionKey) return [];
  const url = buildSessionFileUrl({
    origin: args.origin,
    basePath: args.basePath,
    sessionKey: args.sessionKey,
  });
  const resp = await fetch(url, {
    headers: authHeaders(args.token),
    credentials: "include",
  });
  if (!resp.ok) return [];
  const json = (await resp.json()) as { ok?: boolean; files?: SessionFileRecord[] };
  return Array.isArray(json.files) ? json.files : [];
}

// ---------------------------------------------------------------------------
// Presentation helpers (pure — unit-testable without a DOM)
// ---------------------------------------------------------------------------

/** `812 B` / `2.1 MB` / `25 MB` */
export function formatAttachmentBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * Type icon for a file that has no thumbnail.
 *
 * JAISIU-4435 — this now delegates to the shared
 * {@link attachmentIconNameForKind} mapper, which understands **both** the
 * gateway family (`SessionFileKind`) and the render family
 * (`AttachmentKind`). The previous local switch had no `pdf` case (a PDF
 * is `document` on the wire, so it always showed the generic document
 * glyph) and fell through to `archive` for every render kind.
 *
 * Kept as a named export because the Files tree and the chips already
 * import it.
 */
export function sessionFileIconName(kind: SessionFileKind | string | undefined): IconName {
  return attachmentIconNameForKind(kind);
}

/** Short human label for the media type, used next to the filename. */
export function mediaTypeLabel(mediaType: string | undefined): string {
  if (!mediaType) return "";
  const cleaned = mediaType.split(";")[0]?.trim() ?? "";
  if (cleaned === "application/octet-stream") return "";
  const subtype = cleaned.split("/")[1] ?? "";
  return subtype.toUpperCase().slice(0, 8) || cleaned;
}

/** `rows.csv · 2.1 MB · CSV` — the chip and transcript sub-line. */
export function describeSessionFile(file: {
  filename: string;
  size: number;
  mediaType?: string;
}): string {
  return [file.filename, formatAttachmentBytes(file.size), mediaTypeLabel(file.mediaType)]
    .filter(Boolean)
    .join(" · ");
}

/** Trim a filename for a chip without losing its extension. */
export function shortFilename(name: string, max = 34): string {
  if (name.length <= max) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot) : "";
  if (ext.length >= max) return `…${name.slice(-max)}`;
  const head = Math.max(4, max - ext.length - 1);
  return `${name.slice(0, head)}…${ext}`;
}

/** Strip directory separators so a multipart filename is a basename. */
export function basenameForUpload(name: string): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "upload";
  const base = trimmed.split(/[\\/]/).pop()?.trim() ?? "";
  return base || "upload";
}
